import { useState, useEffect } from 'react';
import { UserRole, Permission } from '../../types/auth';
import { Users, Plus, Search, Edit, Trash2, User as UserIcon, Clock, ClipboardCheck, FileText } from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { AddUserForm, UserFormData } from './AddAdminForm';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import { createAdminUser, updateAdminUser, deleteAdminUser, AdminUser, getPartnersList } from '../../services/admin';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { getChangedFields } from '../../utils/formUtils';
import Select from 'react-select';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { fetchAdminsThunk, forceRefreshAdmins } from '../../features/admins/adminsSlice';
import { useMemo } from 'react';
import { Category, getCategories } from '../../services/caregory';

// Define the pagination and sorting props for better type safety
interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (size: number) => void;
}

interface SortingProps {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (col: string, order: 'asc' | 'desc') => void;
}

// Creating a PermissionGroup interface and permission groups for the edit form
interface PermissionGroup {
  label: string;
  permissions: {
    view: Permission;
    manage?: Permission;
  }[];
}

const ManageAdmins = () => {
  const dispatch = useAppDispatch();

  // Get admins state from Redux
  const { users, loading, hasFetched, error } = useAppSelector(
    (state) => state.admins
  );

  // Get auth state
  const { user, token } = useAppSelector(state => state.auth);
  const currentUserRole = user?.role || UserRole.ADMIN;

  // Table & UI states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Modal & editing states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<AdminUser | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Categories list
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Viewing logs
  const [viewingLogs, setViewingLogs] = useState(false);

  // Fetch admins only once if not fetched
  useEffect(() => {
    if (!hasFetched && token) {
      dispatch(fetchAdminsThunk(token));
    }
  }, [hasFetched, token, dispatch]);

  // Refresh admins manually
  const handleRefresh = () => {
    if (token) {
      dispatch(forceRefreshAdmins());
      dispatch(fetchAdminsThunk(token));
    }
  };



  // Permission groups for the edit form
  const permissionGroups: PermissionGroup[] = [
    {
      label: 'Dashboard',
      permissions: [
        { view: 'view-dashboard' as Permission, manage: 'manage-dashboard' as Permission }
      ]
    },
    {
      label: 'Admin Management',
      permissions: [
        { view: 'view-manage-admin' as Permission, manage: 'manage-manage-admin' as Permission }
      ]
    },
    {
      label: 'CleverPe Supply+',
      permissions: [
        { view: 'view-cleverpe-supply+' as Permission, manage: 'manage-cleverpe-supply+' as Permission }
      ]
    },
    {
      label: 'Business Network',
      permissions: [
        { view: 'view-partner' as Permission, manage: 'manage-partner' as Permission },
        { view: 'view-merchant' as Permission, manage: 'manage-merchant' as Permission },
        { view: 'view-kycpending-merchant' as Permission },
        // { view: 'view-brand' as Permission, manage: 'manage-brand' as Permission }
      ]
    },
    {
      label: 'Customers',
      permissions: [
        { view: 'view-customer' as Permission },
        { view: 'view-store-approvals' as Permission, manage: 'manage-store-approvals' as Permission }
      ]
    },
    {
      label: 'Orders and Transactions',
      permissions: [
        // { view: 'view-scheme' as Permission, manage: 'manage-scheme' as Permission },
        // { view: 'view-product' as Permission, manage: 'manage-product' as Permission },
        { view: 'view-order' as Permission, manage: 'manage-order' as Permission },
        { view: 'view-gateway-order' as Permission, manage: 'manage-gateway-order' as Permission }
      ]
    },
    {
      label: 'Products and Schemes',
      permissions: [
        { view: 'view-scheme' as Permission, manage: 'manage-scheme' as Permission },
        { view: 'view-product' as Permission, manage: 'manage-product' as Permission },
        { view: 'view-scheme-bucket' as Permission, manage: 'manage-scheme-bucket' as Permission },
        { view: 'view-category' as Permission, manage: 'manage-category' as Permission }
      ]
    },
    {
      label: 'Tools',
      permissions: [
        { view: 'view-calculator' as Permission }
      ]
    },


    {
      label: 'Reports',
      permissions: [
        { view: 'view-pre-approvals-report' as Permission },
        { view: 'view-settlements-report' as Permission },
        { view: 'view-merchant-data-report' as Permission }
      ]
    },
    {
      label: 'Enquiries and Support',
      permissions: [
        { view: 'view-grievance-redressals' as Permission, manage: 'manage-grievance-redressals' as Permission }
      ]
    },
    {
      label: 'API Monitoring',
      permissions: [
        { view: 'view-external-api-logs' as Permission }
      ]
    },
  ];

  // Add a function to handle permission checkbox change in the edit form
  const handlePermissionChange = (permission: Permission, checked: boolean) => {
    if (!editData) return;

    setEditData(prev => {
      if (!prev) return prev;

      const currentPermissions = prev.dashPermissions || [];

      if (checked) {
        // Add permission if checked
        return {
          ...prev,
          dashPermissions: [...currentPermissions, permission]
        };
      } else {
        // Remove permission if unchecked
        return {
          ...prev,
          dashPermissions: currentPermissions.filter((p: string) => p !== permission)
        };
      }
    });
  };

  // Handle groups selection change for editing
  const handleGroupsChange = (selectedOptions: any) => {
    if (!editData) return;

    const selectedGroups = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];

    setEditData(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        groups: selectedGroups
      };
    });
  };

  // Fetch users based on role
  // const fetchUsers = async () => {
  //   setIsLoading(true);
  //   setError(null);

  //   try {
  //     // Make a call to the real API endpoint using our service
  //     const adminUsers = await fetchAdminUsers(token);

  //     // Apply search filter if provided
  //     let filteredUsers = adminUsers;
  //     if (searchQuery) {
  //       const query = searchQuery.toLowerCase();
  //       filteredUsers = adminUsers.filter((user: AdminUser) =>
  //         (user.name && user.name.toLowerCase().includes(query)) ||
  //         (user.mobileNo && user.mobileNo.includes(query))
  //       );
  //     }

  //     // Apply sorting
  //     filteredUsers.sort((a: AdminUser, b: AdminUser) => {
  //       const aValue = a[sortBy as keyof AdminUser];
  //       const bValue = b[sortBy as keyof AdminUser];

  //       if (typeof aValue === 'string' && typeof bValue === 'string') {
  //         return sortOrder === 'asc'
  //           ? aValue.localeCompare(bValue)
  //           : bValue.localeCompare(aValue);
  //       }

  //       return 0;
  //     });

  //     // Apply pagination
  //     const startIndex = (page - 1) * pageSize;
  //     const endIndex = startIndex + pageSize;
  //     const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  //     setUsers(paginatedUsers);
  //     setTotal(filteredUsers.length);
  //   } catch (error) {
  //     console.error('Error fetching admin users:', error);
  //     setError('Failed to fetch admin users. Please try again later.');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // Initial fetch
  // useEffect(() => {
  //   fetchUsers();
  // }, [currentUserRole, page, pageSize, searchQuery, sortBy, sortOrder, token]);

  // Filter & sort
  const filteredUsers = useMemo(() => {
    let data = [...users];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.mobileNo?.includes(q)
      );
    }

    data.sort((a: any, b: any) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortOrder === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return 0;
    });

    return data;
  }, [users, searchQuery, sortBy, sortOrder]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const total = filteredUsers.length;

  useEffect(() => {
    if (!hasFetched && token) {
      dispatch(fetchAdminsThunk(token));
    }
  }, [hasFetched, token, dispatch]);


  // Fetch partners list when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const result = await getCategories({ page: 1, pageSize: 100 });

        const options = (result.data || []).map((cat: Category) => ({
          value: cat.code,           // value used internally
          label: `${cat.code} - ${cat.name}`, // label shown in dropdown
        }));

        setCategories(options);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);



  // Handle input change for editing
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!editData) return;

    const { name, value } = e.target;

    // Handle special cases for select inputs
    if (name === 'role') {
      // Update role related fields
      setEditData(prev => {
        if (!prev) return prev;

        if (value === UserRole.SUPERADMIN) {
          return {
            ...prev,
            isSuperUser: true,
            role: value
          };
        } else if (value === UserRole.ADMIN) {
          return {
            ...prev,
            isSuperUser: false,
            role: value
          };
        } else if (value === UserRole.CLEVERPE_USER) {
          return {
            ...prev,
            isSuperUser: false,
            role: value
          };
        }

        return prev;
      });
      return;
    }

    // For regular inputs
    setEditData(prev => {
      if (!prev) return prev;
      return { ...prev, [name]: value };
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    if (!editData) return false;

    const newErrors: Record<string, string> = {};

    // Name validation
    if (!editData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (editData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    // Email validation
    if (editData.email !== undefined) {
      if (!editData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    // Mobile validation
    if (!editData.mobileNo.trim()) {
      newErrors.mobileNo = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(editData.mobileNo)) {
      newErrors.mobileNo = 'Please enter a valid 10-digit mobile number';
    }

    // Permissions validation
    if (!editData.dashPermissions || editData.dashPermissions.length === 0) {
      newErrors.dashPermissions = 'At least one permission must be selected';
    }

    // Group permissions validation for cleverpeuser role
    if (editData.role === UserRole.CLEVERPE_USER && (!editData.groups || editData.groups.length === 0)) {
      newErrors.groups = 'At least one group permission must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle row click
  const handleRowClick = (user: AdminUser) => {
    setSelectedUser(user);

    // Initialize editData with correct values including explicit role
    let roleValue = UserRole.ADMIN; // Default to Admin
    if (user.isSuperUser) {
      roleValue = UserRole.SUPERADMIN;
    } else if (user.role === UserRole.CLEVERPE_USER) {
      roleValue = UserRole.CLEVERPE_USER;
    }

    setEditData({
      ...user,
      role: roleValue
    });

    setIsEditing(false);
    setShowDetailsDialog(true);
  };

  // Handle add user
  const handleAddUser = () => {
    setShowAddModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    setShowDeleteDialog(true);
  };

  // Handle closing details dialog
  const handleCloseDetails = () => {
    setShowDetailsDialog(false);
    setIsEditing(false);
  };

  // Handle edit user
  const handleEditUser = () => {
    // No need to clear the role, we want to keep the previous role
    setIsEditing(true);
  };

  // Handle save user changes
  const handleSave = async () => {
    if (!validateForm() || !editData || !selectedUser) return;

    setIsUpdating(true);
    // setError(null);

    try {
      // Check if trying to modify a superadmin when not a superadmin
      if (selectedUser.isSuperUser && currentUserRole !== UserRole.SUPERADMIN) {
        throw new Error('You do not have permission to modify a Super Admin account');
      }

      // Determine the role value based on dropdown selection
      let roleValue = UserRole.ADMIN; // Default to Admin
      if (editData.role === UserRole.CLEVERPE_USER) {
        roleValue = UserRole.CLEVERPE_USER;
      } else if (editData.role === UserRole.SUPERADMIN || editData.isSuperUser) {
        roleValue = UserRole.SUPERADMIN;
      }

      // Create the formatted edit data object
      const formattedEditData = {
        name: editData.name,
        email: editData.email,
        mobileNo: editData.mobileNo,
        role: roleValue,
        suspended: editData.suspended,
        activated: editData.activated,
        verified: editData.verified,
        dashPermissions: editData.dashPermissions || [], // Use dashPermissions instead of permissions
        groups: editData.groups || [],
        lender: editData.lender || []
      };

      // Add groups array for cleverpeuser role
      if (roleValue === UserRole.CLEVERPE_USER) {
        formattedEditData.groups = editData.groups || [];
      }

      // Create the original data object in the same format
      const originalData = {
        name: selectedUser.name,
        email: selectedUser.email || '',
        mobileNo: selectedUser.mobileNo,
        role: selectedUser.role || (selectedUser.isSuperUser ? UserRole.SUPERADMIN : UserRole.ADMIN),
        suspended: selectedUser.suspended,
        activated: selectedUser.activated,
        verified: selectedUser.verified,
        dashPermissions: selectedUser.dashPermissions || [],
        groups: selectedUser.groups || []
      };

      // Add groups array to original data if role is cleverpeuser
      if (selectedUser.role === UserRole.CLEVERPE_USER) {
        originalData.groups = selectedUser.groups || [];
      }

      // Get only the changed fields
      const updateData = getChangedFields(originalData, formattedEditData);

      console.log('Original user data:', originalData);
      console.log('Edited user data:', formattedEditData);
      console.log('Only sending changed fields:', updateData);

      // Only proceed if there are actually changes
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes detected');
        setIsUpdating(false);
        setIsEditing(false);
        return;
      }

      // Make API call
      const updatedUser = await updateAdminUser({
        _id: selectedUser._id,
        mobileNo: selectedUser.mobileNo
      }, updateData, token);

      // Update the user in our local state
      // setUsers(prev => prev.map(user =>
      //   user._id === updatedUser._id ? updatedUser : user
      // ));

      // Update the selected user for the view
      setSelectedUser(updatedUser);

      // Exit editing mode but keep details modal open
      setIsEditing(false);
      setIsUpdating(false);

      // Refresh users in the background
      // fetchUsers();

      // Show success message
      if (token) {
        dispatch(forceRefreshAdmins());
        dispatch(fetchAdminsThunk(token));
      }
      toast.success('User updated successfully');
    } catch (error: any) {
      console.error('Error updating user:', error);

      // Show error message
      const errorMessage = error.message || 'Failed to update user';
      // setError(errorMessage);
      toast.error(errorMessage);

      setIsUpdating(false);
    }
  };

  // Handle status field change
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!editData) return;

    const status = e.target.value;
    console.log('Status changed to:', status);

    // Update the appropriate status fields based on the selected status
    // This matches how the backend expects these fields to be set
    setEditData(prev => {
      if (!prev) return prev;

      switch (status) {
        case 'active':
          return {
            ...prev,
            suspended: false,
            activated: true,
            verified: prev.verified // Keep verified state
          };
        case 'suspended':
          return {
            ...prev,
            suspended: true,
            activated: false, // Set activated to false when suspended
            verified: prev.verified // Keep verified state
          };
        case 'pending':
          return {
            ...prev,
            suspended: false,
            activated: false,
            verified: false // Not verified if pending
          };
        default:
          return prev;
      }
    });
  };

  // Get available roles based on current user role
  const getAvailableRoles = () => {
    if (currentUserRole === UserRole.SUPERADMIN) {
      return [
        // Removed SuperAdmin option since it can't be created/edited
        { value: UserRole.ADMIN, label: 'Admin' },
        { value: UserRole.CLEVERPE_USER, label: 'CleverPe User' },
      ];
    } else if (currentUserRole === UserRole.ADMIN) {
      return [
        { value: UserRole.CLEVERPE_USER, label: 'CleverPe User' },
      ];
    }

    return [];
  };

  // Check if user can edit role
  const canEditRole = () => {
    if (!selectedUser) return false;

    // Super Admin users' roles cannot be changed
    if (selectedUser.isSuperUser) return false;

    // Only check for manage-manage-admin permission
    const hasManageAdminsPermission = user?.dashPermissions?.includes('manage-manage-admin') || false;

    // Any user with manage-manage-admin permission can edit roles of non-SuperAdmin users
    return hasManageAdminsPermission;
  };

  // Check if user can delete the selected user
  const canDeleteUser = () => {
    if (!selectedUser) return false;

    // Super Admin users cannot be deleted
    if (selectedUser.isSuperUser) return false;

    // Only check for manage-manage-admin permission
    const hasManageAdminsPermission = user?.dashPermissions?.includes('manage-manage-admin') || false;

    // Any user with manage-manage-admin permission can delete non-SuperAdmin users
    return hasManageAdminsPermission;
  };

  // Function to check if user can add new admins
  const canAddAdmin = () => {
    // Only check for manage-manage-admin permission
    const hasManageAdminsPermission = user?.dashPermissions?.includes('manage-manage-admin') || false;

    // Any user with manage-manage-admin permission can add admins
    return hasManageAdminsPermission;
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format date with time for display
  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setIsDeleting(true);

    try {
      // Delete using both ID and mobile number for reliability
      await deleteAdminUser({
        _id: selectedUser._id,
        mobileNo: selectedUser.mobileNo
      }, token);

      // Toast is now handled in the service function

      // Remove from users list
      // setUsers(prev => prev.filter(user => user._id !== selectedUser._id));
      // setTotal(prev => prev - 1);
      if (token) {
        dispatch(forceRefreshAdmins());
        dispatch(fetchAdminsThunk(token));
      }


      // Close modals
      setShowDeleteDialog(false);
      setShowDetailsDialog(false);
    } catch (error: any) {
      console.error('Error deleting user:', error);

      // Set error state
      // setError(error.message || 'Failed to delete admin user');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle create user (add admin)
  const handleCreateUser = async (formData: UserFormData) => {
    try {
      // setIsLoading(true);

      console.log('FormData from AddUserForm:', formData);
      console.log('Activated field in formData:', formData.activated);

      const userToCreate: Partial<AdminUser> = {
        name: formData.name,
        email: formData.email,
        mobileNo: formData.mobile,
        role: formData.role,
        isSuperUser: formData.role === UserRole.SUPERADMIN,
        dashPermissions: formData.dashPermissions,
        suspended: formData.suspended !== undefined ? formData.suspended : false,
        activated: formData.activated !== undefined ? formData.activated : true,
        verified: formData.verified !== undefined ? formData.verified : true
      };

      // Add groups array if role is cleverpeuser
      if (formData.role === UserRole.CLEVERPE_USER && formData.groups && formData.groups.length > 0) {
        userToCreate.groups = formData.groups;
      }
      if (formData.role === UserRole.CLEVERPE_USER && formData.lender && formData.lender.length > 0) {
        userToCreate.lender = formData.lender;
      }

      console.log('User object to send to createAdminUser:', userToCreate);
      console.log('Activated value in userToCreate:', userToCreate.activated);

      await createAdminUser(userToCreate, token);

      // Refresh data after creating
      // fetchUsers();
      if (token) {
        dispatch(forceRefreshAdmins());
        dispatch(fetchAdminsThunk(token));
      }

      setShowAddModal(false);
      toast.success('Admin user created successfully!');
    } catch (error) {
      console.error('Failed to create admin user:', error);
      toast.error('Failed to create admin user');
    } finally {
      // setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col">
        {/* Header section with title and Add Admin button */}
        <div className="flex items-center justify-end mb-6">
          {canAddAdmin() && (
            <Button
              onClick={handleAddUser}
              leftIcon={<Plus size={16} />}
            >
              Add Admin
            </Button>
          )}
        </div>

        {/* Error message above table */}
        {error && (
          <div className="px-4 py-2 mb-4 text-red-700 border border-red-200 rounded-lg bg-red-50">
            {error}
          </div>
        )}

        <CommonTable
          data={paginatedUsers}
          columns={[
            {
              id: 'name',
              header: 'Name',
              cell: ({ row }) => {
                const user = row.original as AdminUser;
                return (
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-8 h-8 mr-3 bg-gray-200 rounded-full">
                      {user.img ? (
                        <img src={user.img} alt={user.name} className="object-cover w-8 h-8 rounded-full" />
                      ) : (
                        <UserIcon size={16} className="text-gray-500" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.mobileNo}</div>
                    </div>
                  </div>
                );
              }
            },
            {
              id: 'role',
              header: 'Role',
              cell: ({ row }) => {
                const user = row.original as AdminUser;

                // Determine the role and styling
                let roleName = 'Admin';
                let styleClasses = 'bg-blue-100 text-blue-800 border border-blue-300';

                if (user.isSuperUser) {
                  roleName = 'Super Admin';
                  styleClasses = 'bg-purple-100 text-purple-800 border border-purple-300';
                } else if (user.role === UserRole.CLEVERPE_USER || user.role === 'cleverpeuser') {
                  roleName = 'CleverPe User';
                  styleClasses = 'bg-green-100 text-green-800 border border-green-300';
                }

                return (
                  <div className={`px-3 py-1 rounded-full text-xs font-medium inline-block ${styleClasses}`}>
                    {roleName}
                  </div>
                );
              }
            },
            {
              id: 'status',
              header: 'Status',
              cell: ({ row }) => {
                const user = row.original as AdminUser;
                return (
                  <div className={`px-2 py-1 rounded-full text-xs font-medium inline-block 
                    ${user.suspended
                      ? 'bg-red-100 text-red-700'
                      : user.activated
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                    {user.suspended
                      ? 'Suspended'
                      : user.activated
                        ? 'Active'
                        : 'Pending'
                    }
                  </div>
                );
              }
            },
            {
              id: 'createdAt',
              header: 'Created On',
              accessorKey: 'createdAt',
              cell: ({ row }) => formatDate((row.original as AdminUser).createdAt)
            },
            {
              id: 'lastLogin',
              header: 'Last Login',
              accessorKey: 'lastLogin',
              cell: ({ row }) => {
                const user = row.original as AdminUser;
                return user.lastLogin ? formatDateTime(user.lastLogin) : 'Never';
              }
            }
          ]}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onRefresh={handleRefresh}
          onSortChange={(col, order) => {
            setSortBy(col);
            setSortOrder(order);
          }}
          enableRowClick={true}
          onRowClick={handleRowClick}
          emptyStateMessage="No admin users found."
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search admins..."
        />
      </div>

      {/* Add Admin Modal */}
      <Dialog
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Admin"
        size="md"
      >
        <AddUserForm
          onSubmit={handleCreateUser}
          onCancel={() => setShowAddModal(false)}
        />
      </Dialog>

      {/* User Details Dialog */}
      {showDetailsDialog && selectedUser && (
        <Dialog
          title={isEditing ? "Edit User" : "User Details"}
          isOpen={showDetailsDialog}
          onClose={handleCloseDetails}
          size="lg"
        >
          <div className="p-5">
            {isEditing ? (
              /* Edit Form */
              <form className="space-y-4">
                {/* Error message */}
                {error && (
                  <div className="p-3 text-sm text-red-700 border border-red-300 rounded-md bg-red-50">
                    {error}
                  </div>
                )}

                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-sm tracking-wider text-gray-500 uppercase font-gilroy-bold">Basic Information</h3>

                  {/* Name */}
                  <div>
                    <label htmlFor="edit-name" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="edit-name"
                      name="name"
                      type="text"
                      value={editData?.name || ''}
                      onChange={handleChange}
                      disabled={isUpdating}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="edit-email" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="edit-email"
                      name="email"
                      type="email"
                      value={editData?.email || ''}
                      onChange={handleChange}
                      disabled={isUpdating}
                      className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                    )}
                  </div>

                  {/* Mobile */}
                  <div>
                    <label htmlFor="edit-mobile" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <span className="text-gray-500">+91</span>
                      </div>
                      <input
                        id="edit-mobile"
                        name="mobileNo"
                        type="tel"
                        value={editData?.mobileNo || ''}
                        onChange={handleChange}
                        disabled={isUpdating}
                        className={`w-full pl-12 pr-3 py-2 border ${errors.mobileNo ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        maxLength={10}
                      />
                    </div>
                    {errors.mobileNo && (
                      <p className="mt-1 text-xs text-red-500">{errors.mobileNo}</p>
                    )}
                  </div>

                  {/* Role */}
                  <div>
                    <label htmlFor="edit-role" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="edit-role"
                      name="role"
                      value={editData?.role || ''}
                      onChange={handleChange}
                      disabled={!canEditRole() || isUpdating}
                      className={`w-full px-3 py-2 border ${errors.role ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${!canEditRole() ? 'bg-gray-100' : ''}`}
                    >
                      {getAvailableRoles().map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                    {errors.role && (
                      <p className="mt-1 text-xs text-red-500">{errors.role}</p>
                    )}
                  </div>

                  {/* Group Permissions field - only shown for CleverPe User role */}
                  {editData?.role === UserRole.CLEVERPE_USER && (
                    <div>
                      <label htmlFor="edit-categories" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
                        Group Category Permissions <span className="text-red-500">*</span>
                      </label>
                      <Select
                        id="edit-categories"
                        isMulti
                        options={categories}
                        value={categories.filter(option => editData?.groups?.includes(option.value))}
                        onChange={handleGroupsChange}
                        className={errors.groups ? 'react-select-error' : ''}
                        classNamePrefix="react-select"
                        placeholder="Select Groups"
                        isLoading={isLoadingCategories}
                        isDisabled={isUpdating || isLoadingCategories}
                        noOptionsMessage={() => "No categories found"}
                      />
                      {errors.groups && (
                        <p className="mt-1 text-xs text-red-500">{errors.groups}</p>
                      )}
                    </div>

                  )}
                  {/* Lender Permissions field - only shown for CleverPe User role */}
                  {editData?.role === UserRole.CLEVERPE_USER && (
                    <div className="mt-4">
                      <label htmlFor="edit-lender" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
                        Lender Permissions <span className="text-red-500">*</span>
                      </label>
                      <Select
                        id="edit-lender"
                        isMulti
                        options={[
                          { value: 'cashe', label: 'Cashe' },
                          { value: 'cashe-ev', label: 'Cashe-EV' },
                          { value: 'zype', label: 'Zype' }
                        ]}
                        value={
                          editData?.lender?.map(l => {
                            const optionMap: Record<string, string> = {
                              cashe: 'Cashe',
                              'cashe-ev': 'Cashe-EV',
                              zype: 'Zype'
                            };
                            return { value: l, label: optionMap[l] || l };
                          }) || []
                        }
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions.map(opt => opt.value);

                          setEditData(prev => {
                            if (!prev) return prev;

                            return {
                              ...prev,
                              lender: selectedValues
                            };
                          });
                        }}
                        className={errors.lender ? 'react-select-error' : ''}
                        classNamePrefix="react-select"
                        placeholder="Select Lenders"
                        isDisabled={isUpdating}
                        noOptionsMessage={() => "No lenders found"}
                      />
                      {errors.lender && (
                        <p className="mt-1 text-xs text-red-500">{errors.lender}</p>
                      )}
                    </div>
                  )}


                  {/* Status */}
                  <div>
                    <label htmlFor="edit-status" className="block mb-1 text-sm font-gilroy-medium text-brand-dark">
                      Account Status
                    </label>
                    <select
                      id="edit-status"
                      name="status"
                      value={editData?.suspended ? 'suspended' : 'active'}
                      onChange={handleStatusChange}
                      disabled={isUpdating}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Active (Account Enabled)</option>
                      <option value="suspended">Suspended (Account Disabled)</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {editData?.suspended
                        ? "Suspended accounts cannot log in to the dashboard."
                        : "Active accounts have full access to the dashboard based on permissions."}
                    </p>
                  </div>
                </div>

                {/* Permissions section */}
                <div className="mt-6">
                  <h3 className="mb-3 text-sm tracking-wider text-gray-500 uppercase font-gilroy-bold">Dashboard Permissions</h3>

                  {errors.dashPermissions && (
                    <p className="mt-1 mb-3 text-xs text-red-500">
                      {errors.dashPermissions}
                    </p>
                  )}

                  <div className={`p-4 border ${errors.dashPermissions ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg max-h-80 overflow-y-auto`}>
                    {permissionGroups.map((group, index) => (
                      <div key={index} className="mb-4">
                        <h3 className="mb-2 font-medium text-gray-700">{group.label}</h3>
                        <div className="space-y-2">
                          {group.permissions.map(perm => (
                            <div key={`${perm.view}`} className="space-y-1">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`perm-${perm.view}`}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                  checked={editData?.dashPermissions?.includes(perm.view) || false}
                                  onChange={(e) => handlePermissionChange(perm.view, e.target.checked)}
                                />
                                <label htmlFor={`perm-${perm.view}`} className="ml-2 text-sm text-gray-700">
                                  View {perm.view.replace('view-', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </label>
                              </div>

                              {perm.manage && (
                                <div className="flex items-center ml-6">
                                  <input
                                    type="checkbox"
                                    id={`perm-${perm.manage}`}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                    checked={editData?.dashPermissions?.includes(perm.manage) || false}
                                    onChange={(e) => {
                                      if (perm.manage) {
                                        handlePermissionChange(perm.manage, e.target.checked);
                                      }
                                    }}
                                  />
                                  <label htmlFor={`perm-${perm.manage}`} className="ml-2 text-sm text-gray-700">
                                    Manage {perm.manage.replace('manage-', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                  </label>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Save and Cancel buttons */}
                <div className="flex justify-end pt-4 space-x-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={handleCloseDetails}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    isLoading={isUpdating}
                  >
                    {isUpdating ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            ) : (
              /* User Details View */
              <div className="space-y-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="p-2 mr-3 bg-indigo-100 rounded-full">
                      <UserIcon className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{selectedUser.name}</h3>
                      <p className="text-sm text-gray-500">{selectedUser.email}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {canEditRole() && (
                      <Button
                        variant="outline"
                        onClick={handleEditUser}
                        size="sm"
                        className="flex items-center"
                      >
                        <Edit size={16} className="mr-1" />
                        Edit
                      </Button>
                    )}
                    {canDeleteUser() && (
                      <Button
                        variant="danger"
                        onClick={handleDeleteConfirm}
                        size="sm"
                        className="flex items-center"
                      >
                        <Trash2 size={16} className="mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>

                {/* Toggle between Info and Logs */}
                <div className="flex items-center mb-4 border-b border-gray-200">
                  <button
                    className={`py-2 px-4 focus:outline-none ${!viewingLogs ? 'text-blue-600 border-b-2 border-blue-600 font-medium' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setViewingLogs(false)}
                  >
                    User Information
                  </button>
                  <button
                    className={`py-2 px-4 focus:outline-none ${viewingLogs ? 'text-blue-600 border-b-2 border-blue-600 font-medium' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setViewingLogs(true)}
                  >
                    Logs
                  </button>
                </div>

                {!viewingLogs ? (
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                      <h4 className="flex items-center mb-4 text-sm font-medium text-gray-700">
                        <UserIcon size={16} className="mr-2 text-blue-600" />
                        User Information
                      </h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-1">
                          <p className="text-xs text-gray-500">Mobile</p>
                          <p className="font-medium">+91 {selectedUser.mobileNo}</p>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                          <p className="text-xs text-gray-500">Role</p>
                          <p className="font-medium">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedUser.isSuperUser
                              ? 'bg-purple-100 text-purple-800'
                              : selectedUser.role === UserRole.CLEVERPE_USER
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                              }`}>
                              {selectedUser.isSuperUser ? 'Super Admin' : (selectedUser.role === UserRole.CLEVERPE_USER ? 'CleverPe User' : 'Admin')}
                            </span>
                          </p>
                        </div>

                        {/* Group Permissions for CleverPe User role */}
                        {selectedUser.role === UserRole.CLEVERPE_USER && (
                          <div className="grid grid-cols-1 gap-1">
                            <p className="text-xs text-gray-500">Group Category Permissions</p>
                            <div>
                              {selectedUser.groups && selectedUser.groups.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {selectedUser.groups.map((group, index) => (
                                    <span key={index} className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 border border-blue-100 rounded-full bg-blue-50">
                                      {group}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm italic text-gray-500">No groups assigned</p>
                              )}
                            </div>
                          </div>
                        )}
                        {selectedUser.role === UserRole.CLEVERPE_USER && (
                          <div className="grid grid-cols-1 gap-1">
                            <p className="text-xs text-gray-500">Lender Permissions</p>
                            <div>
                              {selectedUser.lender && selectedUser.lender.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {selectedUser.lender.map((lender, index) => (
                                    <span key={index} className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 border border-blue-100 rounded-full bg-blue-50">
                                      {lender}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm italic text-gray-500">No lender assigned</p>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 gap-1">
                          <p className="text-xs text-gray-500">Status</p>
                          <div className="flex items-center">
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${selectedUser.suspended
                              ? 'bg-red-500'
                              : selectedUser.activated
                                ? 'bg-green-500'
                                : 'bg-yellow-500'
                              }`}></span>
                            <p className="font-medium">
                              {selectedUser.suspended
                                ? 'Suspended'
                                : selectedUser.activated
                                  ? 'Active'
                                  : 'Pending'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                          <p className="text-xs text-gray-500">Added On</p>
                          <p className="font-medium">{formatDate(selectedUser.createdAt)}</p>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                          <p className="text-xs text-gray-500">Last Login</p>
                          <p className="font-medium">{selectedUser.lastLogin ? formatDateTime(selectedUser.lastLogin) : 'Never'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                      <h4 className="flex items-center mb-4 text-sm font-medium text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Permissions
                      </h4>
                      <div className="pr-1 overflow-y-auto max-h-60">
                        {permissionGroups.map((group, groupIndex) => {
                          // Only show permission groups that have at least one permission for this user
                          const hasGroupPermissions = group.permissions.some(p =>
                            selectedUser.dashPermissions?.includes(p.view) ||
                            (p.manage && selectedUser.dashPermissions?.includes(p.manage))
                          );

                          if (!hasGroupPermissions) return null;

                          return (
                            <div key={groupIndex} className="mb-4 last:mb-0">
                              <h5 className="p-1 mb-2 text-xs font-medium text-gray-700 rounded bg-gray-50">{group.label}</h5>
                              <ul className="ml-1 space-y-2">
                                {group.permissions.map((permissionPair, index) => {
                                  const hasViewPermission = selectedUser.dashPermissions?.includes(permissionPair.view);
                                  const hasManagePermission = permissionPair.manage && selectedUser.dashPermissions?.includes(permissionPair.manage);

                                  if (!hasViewPermission && !hasManagePermission) return null;

                                  return (
                                    <li key={index} className="text-xs">
                                      {hasViewPermission && (
                                        <div className="flex items-start mb-1">
                                          <span className="inline-block bg-blue-100 text-blue-800 rounded-full p-1 mr-1.5">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                          </span>
                                          <span>
                                            View {permissionPair.view.replace('view-', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                          </span>
                                        </div>
                                      )}
                                      {hasManagePermission && (
                                        <div className="flex items-start ml-0">
                                          <span className="inline-block bg-green-100 text-green-800 rounded-full p-1 mr-1.5">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                          </span>
                                          <span>
                                            Manage {permissionPair.manage!.replace('manage-', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                          </span>
                                        </div>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          );
                        })}

                        {!selectedUser.dashPermissions || selectedUser.dashPermissions.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-8">
                            <FileText size={40} className="mb-2 text-gray-300" />
                            <p className="text-sm text-gray-500">No permissions assigned</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-hidden bg-white border border-gray-200 rounded-md">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <h3 className="text-sm font-medium text-gray-700">Logs</h3>
                    </div>

                    {selectedUser.logs && selectedUser.logs.length > 0 ? (
                      <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
                        {selectedUser.logs.slice().reverse().map((log, index) => (
                          <div key={index} className="p-4 hover:bg-gray-50">
                            <div className="flex items-start">
                              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 mr-3 text-blue-600 bg-blue-100 rounded-full">
                                <ClipboardCheck size={16} />
                              </div>
                              <div>
                                <p className="text-sm text-gray-700">{log}</p>
                                <div className="flex items-center mt-1 text-xs text-gray-500">
                                  <Clock size={12} className="mr-1" />
                                  <span>
                                    {log.includes(" at ") ? log.split(" at ")[1] : ""}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="flex items-center justify-center w-16 h-16 mb-4 text-gray-400 bg-gray-100 rounded-full">
                          <ClipboardCheck size={24} />
                        </div>
                        <p className="text-sm text-gray-500">No Logs available for this user</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {selectedUser && (
        <Dialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          title="Confirm Delete"
          size="sm"
        >
          <div className="space-y-4 no-scrollbar">
            <p className="text-gray-700">
              Are you sure you want to delete user "{selectedUser.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end pt-4 space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                isLoading={isDeleting}
                onClick={handleDeleteUser}
              >
                {isDeleting ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default ManageAdmins; 